const SCHEMA_VERSION = "analytics_v1";

type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsParams = Record<string, AnalyticsValue>;

const ROUTE_PAGE_MAP: Array<{ pattern: RegExp; page: string }> = [
  { pattern: /^\/login$/i, page: "login_page" },
  { pattern: /^\/verify$/i, page: "otp_verification_page" },
  { pattern: /^\/complete_profile$/i, page: "complete_profile_page" },
  { pattern: /^\/home$/i, page: "home_page" },
  { pattern: /^\/pricing$/i, page: "price_list_page" },
  { pattern: /^\/profile$/i, page: "profile_page" },
  { pattern: /^\/editProfile$/i, page: "edit_profile_page" },
  { pattern: /^\/orders$/i, page: "order_history_page" },
  { pattern: /^\/orders\/(ongoing|upcoming|completed)\/[^/]+$/i, page: "order_details_page" },
  { pattern: /^\/order\/create$/i, page: "create_order_page" },
  { pattern: /^\/address$/i, page: "address_history_page" },
  { pattern: /^\/address\/add$/i, page: "add_address_page" },
  { pattern: /^\/address\/[^/]+$/i, page: "edit_address_page" },
];

function hasGtag(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function defaultParams(): AnalyticsParams {
  return {
    platform: "web",
    schema_version: SCHEMA_VERSION,
  };
}

function devLog(name: string, params: AnalyticsParams): void {
  if (import.meta.env.DEV) {
    console.info("[analytics]", name, params);
  }
}

export function logEvent(name: string, params: AnalyticsParams = {}): void {
  const payload = { ...defaultParams(), ...params };
  devLog(name, payload);
  if (!hasGtag()) return;
  window.gtag!("event", name, payload);
}

export function setUserProperties(): void {
  if (!hasGtag()) return;
  window.gtag!("set", "user_properties", { app_type: "web" });
}

export function routeToPageKey(pathname: string): string {
  const matched = ROUTE_PAGE_MAP.find((entry) => entry.pattern.test(pathname));
  if (matched) return matched.page;
  return pathname === "/" ? "splash_page" : "unknown_page";
}

export function trackPageView(pathname: string): void {
  const page = routeToPageKey(pathname);
  const payload = {
    page_path: pathname,
    page_title: page,
    page_location: typeof window !== "undefined" ? window.location.href : pathname,
    ...defaultParams(),
    page,
  };
  devLog("page_view", payload);
  if (!hasGtag()) return;
  window.gtag!("event", "page_view", payload);
}

export function logAppOpened(): void {
  logEvent("app_opened");
}

export function logSessionStarted(): void {
  logEvent("session_started");
}

export function logBookingStarted(): void {
  logEvent("booking_started");
}

export function logBookingConfirmTapped(itemCount: number, express: boolean): void {
  logEvent("booking_confirm_tapped", { item_count: itemCount, express });
}

export function logBookingCreated(
  orderId: string | number,
  itemCount: number,
  express: boolean,
  hasCoupon: boolean,
): void {
  logEvent("booking_created", {
    order_id: String(orderId),
    item_count: itemCount,
    express,
    has_coupon: hasCoupon,
  });
}

export function logBookingCreateFailed(errorCode: string): void {
  logEvent("booking_create_failed", { error_code: errorCode });
}

export function logPaymentOptionSelected(method: "cod" | "online"): void {
  logEvent("payment_option_selected", { method });
}

export function logPaymentProceedTapped(orderId: string | number, method: "cod" | "online"): void {
  logEvent("payment_proceed_tapped", { order_id: String(orderId), method });
}

export function logPaymentSuccess(orderId: string | number, method: "cod" | "online"): void {
  logEvent("payment_success", { order_id: String(orderId), method });
}

export function logPaymentFailed(orderId: string | number, reason: string): void {
  logEvent("payment_failed", { order_id: String(orderId), reason });
}

export function logPriceTabViewed(
  serviceId: string | number,
  serviceName: string,
  source: "default" | "tab_tap" | "swipe",
): void {
  logEvent("price_tab_viewed", {
    service_id: String(serviceId),
    service_name: serviceName,
    source,
  });
}

export function logPricePageViewed(): void {
  logEvent("price_page_viewed");
}

export function logPriceBookTapped(serviceId: string | number, serviceName: string): void {
  logEvent("price_book_tapped", { service_id: String(serviceId), service_name: serviceName });
}

export function logCouponApplied(code: string): void {
  logEvent("coupon_applied", { code });
}

export function logCouponApplyFailed(code: string, reason: string): void {
  logEvent("coupon_apply_failed", { code, reason });
}

export function logReferralCodeApplied(code: string): void {
  logEvent("referral_code_applied", { code });
}

export function logReferralSheetOpened(): void {
  logEvent("referral_sheet_opened");
}

export function logReferralCodeSubmitted(code: string): void {
  logEvent("referral_code_submitted", { code });
}

export function logReferralCodeFailed(code: string, reason: string): void {
  logEvent("referral_code_failed", { code, reason });
}

export function logReferralSkipped(): void {
  logEvent("referral_skipped");
}

export function logAddressSelected(addressId: string | number, tag?: string): void {
  logEvent("address_selected", { address_id: String(addressId), tag });
}

export function logAddressListViewed(): void {
  logEvent("address_list_viewed");
}

export function logAddressAdded(tag?: string): void {
  logEvent("address_added", { tag });
}

export function logAddressUpdated(addressId: string | number, tag?: string): void {
  logEvent("address_updated", { address_id: String(addressId), tag });
}

export function logAddressDeleted(addressId: string | number, tag?: string): void {
  logEvent("address_deleted", { address_id: String(addressId), tag });
}

export function logNavTabSelected(tabName: string): void {
  logEvent("nav_tab_selected", { tab_name: tabName });
}

export function logCtaTapped(cta: string, page: string, params: AnalyticsParams = {}): void {
  logEvent("cta_tapped", { cta, page, ...params });
}
